import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Youtube } from "lucide-react";
import { setPath } from "./setPath.js";

const LANGS = [
  { code: "pt", flag: "🇵🇹", label: "PT" },
  { code: "en", flag: "🇬🇧", label: "EN" },
  { code: "es", flag: "🇪🇸", label: "ES" },
];

// Admin: external link button (YouTube/other) inside the "APRENDER A JOGAR" block.
// Persists cfg.online_secondary_section.link_button_* (enabled/label/url per language).
export function LearnLinkButtonPanel({ cfg, setCfg }) {
  const sec = cfg.online_secondary_section || {};
  const set = (k, v) => setCfg((prev) => setPath(prev, ["online_secondary_section", k], v));
  const anyOn = LANGS.some(({ code }) => !!sec[`link_button_enabled_${code}`]);

  return (
    <div className={`rounded-lg border p-3 space-y-3 ${anyOn ? "border-red-300 bg-red-50/40" : "border-slate-200 bg-slate-50/40"}`} data-testid="learn-link-panel">
      <div className="flex items-center gap-2">
        <Youtube className="w-4 h-4 text-red-600" />
        <span className="text-xs font-bold uppercase tracking-wide text-slate-700">Botão de link externo (YouTube ou outro) — por idioma</span>
      </div>
      <p className="text-[11px] text-slate-500">
        Aparece por baixo do vídeo/cards deste bloco e abre num novo separador. Liga/desliga em cada idioma; se o URL de um idioma estiver vazio, usa o de PT.
      </p>
      <div className="space-y-2">
        {LANGS.map(({ code, flag, label }) => {
          const on = !!sec[`link_button_enabled_${code}`];
          return (
            <div key={code} className={`grid grid-cols-[auto_auto_1fr_2fr] items-center gap-2 rounded-md border px-2 py-1.5 ${on ? "bg-white border-red-200" : "bg-white/60 border-slate-200"}`}>
              <span className="text-xs font-mono w-10">{flag} {label}</span>
              <Switch checked={on} onCheckedChange={(v) => set(`link_button_enabled_${code}`, !!v)} data-testid={`learn-link-enabled-${code}`} />
              <Input
                value={sec[`link_button_label_${code}`] || ""}
                onChange={(e) => set(`link_button_label_${code}`, e.target.value)}
                placeholder="Texto do botão"
                className="h-8 text-xs"
                data-testid={`learn-link-label-${code}`}
              />
              <Input
                value={sec[`link_button_url_${code}`] || ""}
                onChange={(e) => set(`link_button_url_${code}`, e.target.value)}
                placeholder="https://www.youtube.com/watch?v=… ou outro link"
                className="h-8 text-xs"
                data-testid={`learn-link-url-${code}`}
              />
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 pt-1">
        <div>
          <Label className="text-xs">Ícone</Label>
          <select
            value={sec.link_button_icon || "youtube"}
            onChange={(e) => set("link_button_icon", e.target.value)}
            className="w-full h-8 rounded border border-slate-300 bg-white px-2 text-xs"
            data-testid="learn-link-icon"
          >
            <option value="youtube">YouTube</option>
            <option value="play">Play</option>
            <option value="link">Link externo</option>
            <option value="none">Sem ícone</option>
          </select>
        </div>
        <div>
          <Label className="text-xs">Cor fundo</Label>
          <div className="flex items-center gap-1">
            <input type="color" value={sec.link_button_bg || "#FF0000"} onChange={(e) => set("link_button_bg", e.target.value)} className="w-9 h-8 rounded border" data-testid="learn-link-bg" />
            <Input value={sec.link_button_bg || "#FF0000"} onChange={(e) => set("link_button_bg", e.target.value)} className="h-8 text-xs font-mono" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Cor texto</Label>
          <div className="flex items-center gap-1">
            <input type="color" value={sec.link_button_color || "#FFFFFF"} onChange={(e) => set("link_button_color", e.target.value)} className="w-9 h-8 rounded border" data-testid="learn-link-color" />
            <Input value={sec.link_button_color || "#FFFFFF"} onChange={(e) => set("link_button_color", e.target.value)} className="h-8 text-xs font-mono" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Tamanho texto (px)</Label>
          <Input type="number" min={10} max={32} value={sec.link_button_size ?? 15} onChange={(e) => set("link_button_size", Number(e.target.value) || 15)} className="h-8 text-xs" data-testid="learn-link-size" />
        </div>
        <div>
          <Label className="text-xs">Espaço acima (px) <span className="opacity-60">(negativo sobrepõe ao vídeo)</span></Label>
          <Input type="number" min={-300} max={80} value={sec.link_button_margin_top ?? 12} onChange={(e) => set("link_button_margin_top", Math.max(-300, Math.min(80, Number(e.target.value) || 0)))} className="h-8 text-xs" data-testid="learn-link-margin" />
        </div>
      </div>
    </div>
  );
}
